import { useState } from "react";

import { useChatClass } from "../../theme";
import { Button } from "../Button";
import { ChatInput } from "../ChatInput";
import { Container, Watermark } from "./styled";

export interface FooterProps {
  /** If `true`, shows a watermark "Chatbot by TOMIS" */
  withWatermark: boolean;
  /** If `true`, shows a prompt to start a new chat by calling the {@link onStart} handler.
   * If `false`, renders controls for the user to write a response. */
  hasEnded?: boolean;
  /** A callback to start a new conversation */
  /** TOMIS, Yonder or other company name */
  poweredByName?: string;
  /** URL (ideally with UTM params) for the company */
  poweredByUrl?: string;
  onStart?: (() => Promise<void>) | undefined;
  /** A callback to submit a user response.
   *
   * @returns A promise that resolves to `true` if the message was sent successfully, or `false` otherwise.
   */
  onSend?: ((message: string) => Promise<boolean>) | undefined;
  /** Called as the user types (each keystroke), for live-chat typing presence. */
  onType?: () => void;
  /** Optional callback when the "Powered by" link is clicked */
  onPoweredByClick?: () => void;
}

interface FooterComponent extends React.FC<FooterProps> {
  Container: typeof Container;
}

/** Footer for the chat widget; displays input controls or a prompt to restart the conversation. */
const Footer: FooterComponent = ({
  withWatermark,
  hasEnded = false,
  poweredByName = "TOMIS",
  poweredByUrl = `https://tomis.tech/?utm_source=TOMIS+ChatBot&utm_medium=Powered+By+TOMIS&utm_campaign=${window.location.hostname}`,
  onStart,
  onSend,
  onType,
  onPoweredByClick,
}: FooterProps) => {
  const chatClass = useChatClass();
  const [message, setMessage] = useState("");
  const [buffering, setBuffering] = useState(false);

  const handleValueChange = (value: string): void => {
    setMessage(value);
    if (value) {
      onType?.();
    }
  };

  const handleSend = async (): Promise<void> => {
    if (!message || buffering || !onSend) return;

    setBuffering(true);
    const toSend = message;
    setMessage("");
    const success = await onSend(message);
    setBuffering(false);

    if (!success) setMessage(toSend);
  };

  return (
    <Container
      $withShadow={Boolean(hasEnded)}
      $withWatermark={withWatermark}
      className={chatClass("footer")}
    >
      {hasEnded ? (
        <Button
          variant="info"
          onClick={onStart}
          aria-label="Start a new chat conversation"
        >
          Start New Chat
        </Button>
      ) : (
        <ChatInput
          id="chat-input"
          value={message}
          placeholder="Message…"
          onValueChange={handleValueChange}
          onSend={handleSend}
          buffering={buffering}
          aria-disabled={buffering}
        />
      )}
      {withWatermark && (
        <Watermark>
          Chatbot ⚡️ by
          <a
            style={{ fontSize: "12px" }}
            target="_blank"
            href={poweredByUrl}
            rel="noopener"
            aria-label={`Powered by ${poweredByName} (opens in new tab)`}
            onClick={onPoweredByClick}
          >
            {poweredByName}
          </a>
        </Watermark>
      )}
    </Container>
  );
};

Footer.Container = Container;

export { Footer };
